import React, { useEffect, useState, useMemo } from 'react';
import {  FaCheckCircle,FaBan, FaChevronLeft, FaChevronRight, FaTrash, FaStar, FaFileInvoice, FaPrint } from 'react-icons/fa';  // Import the icons
import './IndividualReceipt.scss';
import TipSection from './components/TipSection/TipSection';
import CouponSection from './components/CouponSection/CouponSection';
import ActionSection from './components/ActionSection/ActionSection';
import ReceiptDetails from './components/ReceiptDetails/ReceiptDetails';
import RemoveDiscountModal from './components/CouponSection/components/DiscountSection/components/RemoveDiscountModal/RemoveDiscountModal';
import DeleteReceipt from './components/DeleteReceipt/DeleteReceipt';
import {formatCurrency} from '../../../../../../../utils/functions';
import { OverlayTrigger, Tooltip, Spinner } from 'react-bootstrap';
import { InfoCircle, Trash } from 'react-bootstrap-icons';
import { updatePaymentReceiptSuggestedTip } from '../../../../../../../crud/payments_api/payment_receipts';



interface IndividualReceiptProps {
   receipt: any;
   receipts: any[];
   setReceipts: (receipts: any[]) => void;
   setCurrentIndex: (index: number) => void;
   showTipSection?: boolean;
   setIsPayed?: (payed : boolean) => void;
   externalSetPedidos?: (pedidos: any) => void;
   onDelete?: () => void; // Changed from onUndo to onDelete
   acceptChange?: boolean; // NEW: if true, show cash payment modal for cash payments
   onClose?: (targetSelections?: string[]) => void; // NEW: callback to close modal when all receipts are paid
   targetSelections?: string[]; // NEW: array of selection_ids for partial payments
   onGenerateDte?: () => void; // NEW: callback to trigger DTE modal
   showDteQuickAction?: boolean; // NEW: control visibility of DTE quick action button
   onPrintBoleta?: () => void; // NEW: callback to one-click print an SII boleta
   showQuickBoleta?: boolean; // NEW: control visibility of the one-click boleta button
   printingBoleta?: boolean; // NEW: loading state while the boleta is generated/retrieved
}

interface PaymentMethod {
   payment_type_id: number;
   payment_type_name: string;
}

const IndividualReceipt: React.FC<IndividualReceiptProps> = ({
     receipt,
      receipts,
        setCurrentIndex,
        setReceipts,
    showTipSection=true,
    setIsPayed,
    externalSetPedidos,
    onDelete, // Changed from onUndo to onDelete
    acceptChange = false, // Default to false for backwards compatibility
    onClose, // NEW: callback to close modal
    targetSelections, // NEW: array of selection_ids for partial payments
    onGenerateDte, // NEW: DTE generation callback
    showDteQuickAction = false, // NEW: DTE quick action visibility
    onPrintBoleta, // NEW: one-click boleta print callback
    showQuickBoleta = false, // NEW: one-click boleta button visibility
    printingBoleta = false, // NEW: boleta print loading state
       }) => {

   const splitType = receipt?.receipt_metadata?.split_type ?? 'single'
   const [receiptId, setReceiptId] = useState(receipt?.receipt_id ?? 0);
   const [totalCuenta, setTotalCuenta] = useState(receipt?.subtotal ?? 0); // Total account amount
   const [payedFull, setPayedFull] = useState(receipt?.payed_full ?? false);
   const [totalPayed, setTotalPayed] = useState(receipt?.total_payed ?? 0);
   const [discountAmount, setDiscountAmount] = useState(receipt?.discount_amount ?? 0);
   const [manualDiscountAmount, setManualDiscountAmount] = useState(receipt?.manual_discount_amount ?? 0);
   const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod | null>(null);
   const [tipAmount, setTipAmount] = useState(receipt?.suggested_tip ?? 0);
   const [couponAmount, setCouponAmount] = useState(receipt?.coupon_amount ?? 0);
   const [couponCode, setCouponCode] = useState('');
   const [selections, setSelections] = useState<any[]>(receipt?.selections ?? []);
   const [isTransitioning, setIsTransitioning] = useState(false);
   const [transitionDirection, setTransitionDirection] = useState<'left' | 'right' | null>(null);
   const [showRemoveDiscountModal, setShowRemoveDiscountModal] = useState(false);
   const [showDeleteModal, setShowDeleteModal] = useState(false);
   const overallTotal = useMemo(() => {
    return receipts.reduce((sum, r) => sum + (r.total_due ?? 0), 0);
  }, [receipts]);


   useEffect(() => {
       setReceiptId(receipt?.receipt_id ?? 0);
       setTotalCuenta(receipt?.subtotal ?? 0);
       setPayedFull(receipt?.payed_full ?? false);
       setTotalPayed(receipt?.total_payed ?? 0);
       setDiscountAmount(receipt?.discount_amount ?? 0);
       setManualDiscountAmount(receipt?.manual_discount_amount ?? 0);
       setSelectedPaymentMethod(null);
       setTipAmount(receipt?.suggested_tip ?? 0);
       setCouponAmount(receipt?.coupon_amount ?? 0);
       setSelections(receipt?.selections ?? []);
       setCouponCode('');
   }, [receipt]);

   useEffect(() => {
       if (setIsPayed) {
           setIsPayed(payedFull);
       }
   }, [payedFull, setIsPayed]);

   const handleDiscountRemoved = async (removedAmount: number) => {
       const newTotalDiscount = discountAmount - removedAmount;
       const newManualDiscount = Math.max(0, (manualDiscountAmount || discountAmount) - removedAmount);
       
       setDiscountAmount(newTotalDiscount);
       setManualDiscountAmount(newManualDiscount);

       // Recalculate tip proportionally after discount removal
       let newSuggestedTip = tipAmount;
       if (tipAmount > 0) {
           const oldNetBase = totalCuenta - couponAmount - discountAmount;
           const tipRate = oldNetBase > 0 ? tipAmount / oldNetBase : 0;
           const newNetBase = totalCuenta - couponAmount - newTotalDiscount;
           newSuggestedTip = Math.round(newNetBase * tipRate);

           try {
               await updatePaymentReceiptSuggestedTip(receiptId, newSuggestedTip);
               setTipAmount(newSuggestedTip);
           } catch (error) {
               console.error('Error updating tip after discount removal:', error);
           }
       }

       const updatedReceipts = receipts.map((r: any) => {
           if (r.receipt_id === receiptId) {
               return { 
                   ...r, 
                   discount_amount: newTotalDiscount,
                   manual_discount_amount: newManualDiscount,
                   suggested_tip: newSuggestedTip,
               };
           }
           return r;
       });
       setReceipts(updatedReceipts);
   };

   const renderTotalSection = () => {
       return (
           <div className="total-section" >
               <div className="total-row net-consumption">
                   <span className="label">Consumo</span>
                   <span className="value">{formatCurrency(totalCuenta)}</span>
               </div>
               <div className="total-row discount">
                   <span className="label">
                       Descuentos
                       {discountAmount > 0 && (
                           <button
                               className="remove-discount-btn"
                               onClick={() => setShowRemoveDiscountModal(true)}
                               title="Remover descuento"
                           >
                               <FaTrash />
                           </button>
                       )}
                   </span>
                   <span className="value">{formatCurrency(discountAmount)}</span>
               </div>
               <div className="total-row coupon-amount">
                   <span className="label">Cupones</span>
                   <span className="value">{formatCurrency(couponAmount)}</span>
               </div>
               {showTipSection && (<div className="total-row coupon-amount">
                   <span className="label">
                     Propina {totalCuenta > 0 && tipAmount > 0 && (() => {
                       const netBase = totalCuenta - couponAmount - discountAmount;
                       return netBase > 0 ? (
                         <span className="percentage-indicator">
                           ({((tipAmount / netBase) * 100).toFixed(1)}%)
                         </span>
                       ) : null;
                     })()}
                   </span>
                   <span className="value">{formatCurrency(Number(tipAmount))}</span>
               </div>)}
               
               {totalPayed > 0 && (
                <div className="total-row total-payed">
                <span className="label">Pagado</span>
                <span className="value">{formatCurrency(totalPayed)}</span>
            </div>
               )}
               <div className="total-row total-pay">
                   <span className="label">Total a pagar</span>
                   <span className="value">{formatCurrency(Math.max(0, (totalCuenta - couponAmount - Number(discountAmount)) + Number(tipAmount) - totalPayed))}</span>
               </div>
      {splitType === 'custom' && (
        <div className="total-row total-group">
          <span className="label">
            Total pendiente grupo
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id="tooltip-total-grupo">
                  Suma de todos los totales pendientes del grupo
                </Tooltip>
              }
            >
              <InfoCircle
                className="info-icon"
                style={{ cursor: 'pointer', marginLeft: '0.25rem' }}
              />
            </OverlayTrigger>
          </span>
          <span className="value">{formatCurrency(overallTotal)}</span>
        </div>
      )}
           </div>
       );
   };


   // Funciones de navegación para el carousel con animación
   const handlePrevious = () => {
     const idx = receipts.findIndex(r => r.receipt_id === receiptId);
     if (idx > 0) {
       setIsTransitioning(true);
       setTransitionDirection('left');
       setTimeout(() => {
         setCurrentIndex(idx - 1);
         setTimeout(() => {
           setIsTransitioning(false);
           setTransitionDirection(null);
         }, 100);
       }, 150);
     }
   };
   
   const handleNext = () => {
     const idx = receipts.findIndex(r => r.receipt_id === receiptId);
     if (idx < receipts.length - 1) {
       setIsTransitioning(true);
       setTransitionDirection('right');
       setTimeout(() => {
         setCurrentIndex(idx + 1);
         setTimeout(() => {
           setIsTransitioning(false);
           setTransitionDirection(null);
         }, 100);
       }, 150);
     }
   };

   return (
    <div className={`individual-receipt-wrapper ${isTransitioning ? 'transitioning' : ''} ${transitionDirection ? `slide-${transitionDirection}` : ''}`}>
        {/* Main Receipt Content - Scrollable if needed */}
        <div className="individual-receipt-content">
            {/* Navigation arrows OUTSIDE receipt-paper to always be accessible */}
            {receipts.length > 1 && (
                <div className="carousel-navigation">
                    <button
                        onClick={handlePrevious}
                        className={`carousel-arrow carousel-arrow-left ${receipts.findIndex(r => r.receipt_id === receiptId) === 0 ? 'disabled' : ''}`}
                        disabled={receipts.findIndex(r => r.receipt_id === receiptId) === 0}
                        title="Recibo anterior"
                    >
                        <FaChevronLeft />
                    </button>
                    <button
                        onClick={handleNext}
                        className={`carousel-arrow carousel-arrow-right ${receipts.findIndex(r => r.receipt_id === receiptId) === receipts.length - 1 ? 'disabled' : ''}`}
                        disabled={receipts.findIndex(r => r.receipt_id === receiptId) === receipts.length - 1}
                        title="Siguiente recibo"
                    >
                        <FaChevronRight />
                    </button>
                </div>
            )}

            <div className={`receipt-paper ${payedFull ? 'paid' : ''}`}>
                {/* Paid Badge - Visible marker instead of blocking overlay */}
                {payedFull && (
                    <div className="paid-badge">
                        <FaCheckCircle className="paid-icon" />
                        <span>PAGADO</span>
                    </div>
                )}

                {/* Receipt Header */}
                <div className="receipt-header">
                    <div className="receipt-title-section">
                        <h3 className="receipt-title">Recibo de Pago</h3>
                        <div className="receipt-id">#{receipt?.receipt_number ?? receiptId}</div>
                        {/* Counter always below receipt number */}
                        {receipts.length > 1 && (
                            <div className="receipt-counter">
                                {receipts.findIndex(r => r.receipt_id === receiptId) + 1} de {receipts.length}
                            </div>
                        )}
                    </div>
                    
                    {/* Right side controls */}
                    <div className="header-right-controls">
                        {/* Delete button */}
                        {onDelete && !payedFull && (
                            <button
                                onClick={() => setShowDeleteModal(true)}
                                className="delete-button"
                                title="Eliminar recibo"
                            >
                                <Trash />
                            </button>
                        )}
                        
                        {/* One-Click SII Boleta - shows on paid receipts when enabled + SII configured */}
                        {showQuickBoleta && payedFull && onPrintBoleta && (
                            <button
                                onClick={onPrintBoleta}
                                className="boleta-quick-print-btn"
                                title="Imprimir Boleta SII"
                                disabled={printingBoleta}
                            >
                                {printingBoleta ? (
                                    <Spinner animation="border" size="sm" />
                                ) : (
                                    <FaPrint />
                                )}
                                <span>Boleta</span>
                            </button>
                        )}

                        {/* DTE Quick Action - Shows when receipt is paid */}
                        {showDteQuickAction && onGenerateDte && (
                            <button
                                onClick={onGenerateDte}
                                className="dte-quick-action-btn"
                                title="Generar Documento Tributario"
                            >
                                <FaFileInvoice />
                                <span>DTE</span>
                            </button>
                        )}
                    </div>
                </div>

                {/* Membership Info Display */}
                {receipt?.client_name && receipt?.membership_info && (
                    <div className="membership-info-banner">
                        <div className="membership-icon">
                            <FaStar />
                        </div>
                        <div className="membership-details">
                            <div className="client-name">{receipt.client_name}</div>
                            <div className="membership-tier">
                                {receipt.membership_info.membership_name}
                                <span className="discount-badge">
                                    -{receipt.membership_info.discount_percentage}%
                                </span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Receipt Details */}
                <ReceiptDetails 
                    selections={selections}
                    subtotal={totalCuenta}
                    totalReceipts={receipts.length}
                />

                {/* Tip Section - disabled when paid */}
                {showTipSection && 
                    <TipSection
                        receipt={receipt}
                        receipts={receipts}
                        setReceipts={setReceipts}
                        tipAmount={tipAmount}
                        setTipAmount={setTipAmount}
                        totalCuenta={totalCuenta}
                        setTotalCuenta={setTotalCuenta}
                        couponAmount={couponAmount}
                        discountAmount={discountAmount}
                        disabled={payedFull}
                    />
                } 
      
                {/* Coupon Section - disabled when paid */}
                <CouponSection
                    receiptId={receiptId}
                    receipts={receipts}
                    totalCuenta={totalCuenta}
                    setReceipts={setReceipts}
                    couponAmount={couponAmount}
                    setCouponAmount={setCouponAmount}
                    discountAmount={discountAmount}
                    setDiscountAmount={setDiscountAmount}
                    couponCode={couponCode}
                    setCouponCode={setCouponCode}
                    selections={selections}
                    manualDiscountAmount={manualDiscountAmount}
                    setManualDiscountAmount={setManualDiscountAmount}
                    disabled={payedFull}
                    setTipAmount={setTipAmount}
                />

                {/* Receipt Totals */}
                {renderTotalSection()}
            </div>

            {/* Receipt Inactive Overlay - only show for inactive receipts, not for paid ones */}
            {!receipt.active && !payedFull && (
                <div className="payment-overlay inactive-overlay">
                    <FaBan className="check-icon" />
                </div>
            )}
        </div>

        {/* Fixed Footer with Action Buttons */}
        <div className="individual-receipt-footer">
            <ActionSection
                receiptId={receiptId}
                receipt={receipt}
                receipts={receipts}
                setReceipts={setReceipts}
                setCurrentIndex={setCurrentIndex}
                payedFull={payedFull}
                setPayedFull={setPayedFull}
                selectedPaymentMethod={selectedPaymentMethod}
                setSelectedPaymentMethod={setSelectedPaymentMethod}
                couponCode={couponCode}
                externalSetPedidos={externalSetPedidos}
                acceptChange={acceptChange}
                onClose={onClose}
                targetSelections={targetSelections}
            />
        </div>

        {/* Remove Discount Modal */}
        <RemoveDiscountModal
            show={showRemoveDiscountModal}
            onHide={() => setShowRemoveDiscountModal(false)}
            receiptId={receiptId}
            currentDiscountAmount={manualDiscountAmount > 0 ? manualDiscountAmount : discountAmount}
            totalCuenta={totalCuenta}
            onDiscountRemoved={handleDiscountRemoved}
        />

        {/* Delete Receipt Modal */}
        <DeleteReceipt
            show={showDeleteModal}
            onHide={() => setShowDeleteModal(false)}
            receipts={receipts}
            currentReceiptId={receiptId}
            onDelete={onDelete || (() => {})}
        />
    </div>
);
};

export default IndividualReceipt;
